import { Drawer } from "vaul";
import { useLocation } from "react-router-dom";

import { BrandMark } from "@/components/layout/BrandMark";
import { SidebarNav } from "@/components/layout/SidebarNav";
import {
  adminScopeForPath,
  getNavForUser,
  getSectionLabel,
} from "@/components/layout/nav-config";
import { useAuth } from "@/contexts/auth-context";

interface MobileNavDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Left-anchored nav drawer for ≤1120px. Reuses SidebarNav so desktop and mobile
// share one source of truth. vaul handles focus trap, scroll lock, and Escape.
export function MobileNavDrawer({ open, onOpenChange }: MobileNavDrawerProps) {
  const { user, isLegacyGated } = useAuth();
  const { pathname } = useLocation();
  // Story 15.7 — the drawer gets the scope block from the SAME insertion point as
  // the desktop aside (SidebarNav), so the ≤1120px shell is never left without it.
  const isSuperAdmin = user?.role === "super_admin";
  const scope = adminScopeForPath(pathname);
  const items = getNavForUser(user?.role, isLegacyGated, scope);
  const section = getSectionLabel(pathname, user?.role);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} direction="left">
      <Drawer.Portal>
        {/* Tailwind v3 can't apply an opacity modifier to an arbitrary var() color,
            so derive the 40% scrim from the overlay token via color-mix. */}
        <Drawer.Overlay
          className="fixed inset-0 z-40"
          style={{ backgroundColor: "color-mix(in srgb, var(--bg-overlay) 40%, transparent)" }}
        />
        <Drawer.Content
          className="fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col gap-5 border-r border-[color:var(--border-primary)] bg-[color:var(--bg-secondary)] p-4 outline-none"
          aria-label="Navigation"
        >
          <Drawer.Title className="sr-only">Navigation</Drawer.Title>
          <Drawer.Description className="sr-only">
            Primary navigation for the AMPS workspace.
          </Drawer.Description>
          <div className="px-2 pt-1">
            <BrandMark subtitle={section} />
          </div>
          <SidebarNav
            items={items}
            onNavigate={() => onOpenChange(false)}
            adminScope={isSuperAdmin ? scope : undefined}
            pathname={pathname}
          />
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
